import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiCall, generateGrammarDrill } from '../api/client';
import { isCorrectAnswer } from '../utils/lessons';
import TutorChat from './TutorChat';

const buttonClass = 'rounded-lg bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 font-medium disabled:opacity-50';

export default function GrammarDrill({ courseId, lesson, onBack }) {
  const { token, isGuest } = useAuth();
  const [round, setRound] = useState(null);
  const [history, setHistory] = useState([]);
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [results, setResults] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [showTutor, setShowTutor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const request = useRef(null);
  const retryBody = useRef({});
  const input = useRef(null);
  const clarification = useRef(null);
  const count = lesson.drillCount || 10;
  const targetLanguage = lesson.languages?.target || 'the target language';
  const sourceLanguage = lesson.languages?.source || 'your language';
  const question = round?.questions[index];
  const result = results[index];
  const finished = !!round && index === round.questions.length;
  const missed = results.filter(r => !r.correct);

  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => { if (question && !generating) input.current?.focus(); }, [question, generating]);
  useEffect(() => { if (showTutor) clarification.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }); }, [showTutor]);

  async function startRound(body = {}) {
    if (request.current || saving) return;
    const controller = new AbortController();
    request.current = controller;
    retryBody.current = body;
    setError('');
    setStatus({ status: 'generating' });
    setGenerating(true);
    try {
      const generated = await generateGrammarDrill(`/courses/${courseId}/lessons/${lesson.id}/drill`, body, token, {
        signal: controller.signal,
        onStatus: event => { if (!controller.signal.aborted) setStatus(event); },
      });
      if (controller.signal.aborted) return;
      if (finished) setHistory(prev => [...prev, results]);
      setRound(generated);
      setResults([]);
      setIndex(0);
      setAnswer('');
      setSaveError('');
    } catch (err) {
      if (!controller.signal.aborted) setError(err.message || 'Could not generate this round.');
    } finally {
      if (request.current === controller) request.current = null;
      if (!controller.signal.aborted) setGenerating(false);
    }
  }

  function checkAnswer(skip = false) {
    if (!question || result || (!skip && !answer.trim())) return;
    const submitted = skip ? '' : answer.trim();
    setResults(prev => [...prev, { ...question, submitted, correct: !skip && isCorrectAnswer(submitted, question.answers, true) }]);
  }

  function nextQuestion() {
    if (!result) return;
    setIndex(i => i + 1);
    setAnswer('');
  }

  function anotherRound(focused) {
    startRound({
      previousPrompts: round.questions.map(q => q.prompt),
      mistakes: focused ? missed.map(({ prompt, answers, submitted }) => ({ prompt, answers, submitted })) : [],
    });
  }

  async function finishLesson() {
    if (!finished || generating || saving) return;
    setSaving(true);
    setSaveError('');
    const allResults = [...history.flat(), ...results];
    try {
      await apiCall('POST', `/courses/${courseId}/lessons/${lesson.id}/complete`, {
        exerciseResults: {
          score: allResults.filter(r => r.correct).length / allResults.length,
          attempts: allResults.length,
          rounds: history.length + 1,
        },
      }, token);
      onBack();
    } catch (err) {
      setSaveError(err.message || 'Could not save your progress. Please try again.');
      setSaving(false);
    }
  }

  const statusText = status?.status === 'retrying'
    ? `The generated list was not usable. Generating a new one (attempt ${status.attempt} of ${status.maxAttempts})…`
    : status?.status === 'validating' ? 'Checking the generated phrases…'
      : retryBody.current.mistakes?.length ? 'Generating phrases focused on your missed answers…' : `Generating ${count} translation phrases…`;

  const clarificationButton = <button type="button" onClick={() => setShowTutor(v => !v)}
    aria-expanded={showTutor} aria-controls="drill-clarification"
    className="sm:ml-auto rounded-lg border border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900 px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400">
    {showTutor ? 'Close clarification' : 'Ask for clarification'}
  </button>;

  return <div className="min-h-screen bg-amber-50">
    <header className="sticky top-0 z-10 bg-white border-b border-amber-100 p-4 flex items-center gap-3">
      <button onClick={onBack} aria-label="Back to course" className="text-amber-600">←</button>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase text-amber-500">Grammar drill · round {history.length + 1}</p>
        <h1 className="font-bold text-amber-800 truncate">{lesson.title}</h1>
      </div>
    </header>
    <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <section className="rounded-xl bg-amber-100 p-4 text-amber-800">
        <h2 className="font-semibold">Grammar focus: {lesson.grammarPoint}</h2>
        <p className="text-sm mt-1">Translate from {sourceLanguage} into {targetLanguage}. Accents count. Ask for clarification whenever you need it.</p>
      </section>
      {isGuest ? <p className="text-amber-700">Log in to generate a drill and save your progress.</p> : <>
        {generating && <section role="status" aria-live="polite" className="rounded-xl border border-amber-200 bg-white p-5 text-amber-800">
          <p>{statusText}</p>
          <p className="mt-2 text-sm text-amber-600">This may take a little time. Your questions will appear when they are ready.</p>
        </section>}
        {error && <div role="alert" className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-700">
          <p>{error}</p>
          <button onClick={() => startRound(retryBody.current)} disabled={generating || saving} className="underline mt-2">Retry generation</button>
        </div>}
        {!round && !generating && !error && <button onClick={() => startRound()} className={buttonClass}>Start {count}-phrase drill</button>}
        {!generating && question && <section className="bg-white border border-amber-100 rounded-xl p-5 space-y-4">
          <p className="text-sm text-amber-500">Phrase {index + 1} of {round.questions.length}</p>
          <p className="text-xl font-semibold text-amber-900">{question.prompt}</p>
          <form onSubmit={e => { e.preventDefault(); if (result) nextQuestion(); else checkAnswer(); }}>
            <label htmlFor="drill-answer" className="block text-sm text-amber-700 mb-2">Your translation in {targetLanguage}</label>
            <input id="drill-answer" ref={input} value={answer} onChange={e => setAnswer(e.target.value)} readOnly={!!result}
              autoComplete="off" autoCapitalize="off" spellCheck={false} maxLength={500}
              className="w-full rounded-lg border border-amber-300 p-3 focus:outline-none focus:ring-2 focus:ring-amber-400" />
            {result && (
              <div role="status" className={`my-4 p-3 rounded-lg ${result.correct ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                <p className="font-semibold">{result.correct ? 'Correct!' : 'Not quite — here is the translation.'}</p>
                <p>{question.answers.join(' · ')}</p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {result ? (
                <button type="submit" className={buttonClass}>{index === round.questions.length - 1 ? 'See results' : 'Next phrase'}</button>
              ) : <>
                <button type="submit" disabled={!answer.trim()} className={buttonClass}>Check translation</button>
                <button type="button" onClick={() => checkAnswer(true)} className="text-amber-700 underline text-sm">Show answer</button>
              </>}
              {clarificationButton}
            </div>
          </form>
        </section>}
        {finished && <section className="bg-white border border-amber-100 rounded-xl p-5 space-y-4">
          <h2 className="text-xl font-semibold text-amber-800">Round complete</h2>
          <p className="text-amber-700">{results.filter(r => r.correct).length} of {round.questions.length} correct</p>
          {missed.length > 0 ? <>
            <h3 className="font-medium text-amber-800">Questions to work on</h3>
            <ul className="space-y-3">{missed.map((r, i) => <li key={i} className="text-sm border-l-2 border-amber-300 pl-3">
              <p className="font-medium text-amber-900">{r.prompt}</p>
              <p className="text-amber-600">Your answer: {r.submitted || '(skipped)'}</p>
              <p className="text-amber-800">Translation: {r.answers.join(' · ')}</p>
            </li>)}</ul>
            <p className="text-sm text-amber-600">The next focused round uses these mistakes to practise patterns you found difficult.</p>
            <button onClick={() => anotherRound(true)} disabled={generating || saving} className={buttonClass}>Practise my weak spots</button>
          </> : <p className="text-green-700">You got every phrase right. Try a fresh set or finish here.</p>}
          <div className="flex flex-wrap gap-3">
            <button onClick={() => anotherRound(false)} disabled={generating || saving} className="rounded-lg border border-amber-300 text-amber-800 px-4 py-2 disabled:opacity-50">New mixed round</button>
            <button onClick={finishLesson} disabled={generating || saving} className={buttonClass}>{saving ? 'Saving…' : 'Finish & save progress'}</button>
          </div>
          {saveError && <p role="alert" className="text-rose-600">{saveError}</p>}
        </section>}
      </>}
      {!isGuest && (!question || generating) && <div className="flex justify-end">{clarificationButton}</div>}
      {/* Keep the chat mounted when closed so clarification history survives. */}
      <div id="drill-clarification" ref={clarification} hidden={!showTutor}>
        <TutorChat courseId={courseId} lessonId={lesson.id} enabled={!isGuest} showModes={false}
          drillQuestion={question ? { ...question, submitted: result?.submitted ?? answer } : undefined} />
      </div>
    </main>
  </div>;
}
